import { z } from "zod/v4";

/** Pack descriptions qualify a purchase unit; they are not unit conversions.
 * Comparison only: retain the library unit and the research's original label.
 */
export function samePurchaseUnit(a: string, b: string): boolean {
  const key = (value: string) => {
    const text = value.normalize("NFKC").trim().toLowerCase();
    return text.replace(/\([^)]*\)/g, " ")
      .replace(/\b\d+(?:\.\d+)?\s*(?:kg|g|ml|litres?|liters?|l|mm|cm|m)\b/g, " ")
      .replace(/\s+/g, " ").trim() || text;
  };
  return key(a) === key(b);
}

// Integer arithmetic deliberately shared by the editor and server. No formula evaluation.
const decimal = (places: number) => z.number().finite().min(0).max(99_999_999.99)
  .refine(n => Math.abs(n * 10 ** places - Math.round(n * 10 ** places)) < 0.00001,
    `Use at most ${places} decimal places`);
const money = decimal(2);
// Purchase-unit rates can contain fractions of a penny (e.g. nails).
// Keep these until quantity multiplication; payable totals remain pennies.
export const materialUnitPriceSchema = decimal(6);
const quantity = decimal(4);
export const normaliseSteelSectionText = (s: string) => s
  .replace(/\buniversal\s+beams?\b/gi, "UB")
  .replace(/\buniversal\s+columns?\b/gi, "UC")
  .replace(/\b(UB|UC)\s+(?:steel\s+)?(?:beam|column)s?\s+/gi, "$1 ");
export const normaliseSectionKey = (s: string) => normaliseSteelSectionText(s).toUpperCase().replace(/[×*]/g, "X")
  .replace(/[^A-Z0-9.]/g, "").replace(/^(UB|UC)(.+)$/, "$2$1");
export const sectionRateSchema = z.object({
  section: z.string().trim().min(1).max(100),
  grade: z.string().trim().min(1).max(50),
  finish: z.string().trim().min(1).max(100),
  rate_per_metre: money.refine(n => n > 0, "Rate must be positive"),
  supplier: z.string().trim().min(1).max(100),
  source_url: z.url(),
  checked_on: z.iso.date(),
}).strict();
/** Library-owned reference model. Quote measurements never overwrite the reference. */
export const dimensionPricingSchema = z.object({
  measure: z.enum(["length", "area", "volume", "weight", "count"]),
  reference_quantity: quantity.refine(n => n > 0, "Reference measurement must be positive"),
  dimension_labels: z.array(z.string().trim().min(1).max(120)).min(1).max(3),
  reference_dimensions: z.array(z.object({
    label: z.string().trim().min(1).max(120),
    value: quantity.refine(n => n > 0, "Reference dimension must be positive"),
    unit: z.enum(["m", "m²", "m³", "kg", "each", "kg/m"]),
  }).strict()).min(1).max(3).optional(),
  quantity_basis: z.enum(["per_item", "per_work_unit"]).optional(),
  fixed_cost: money.default(0),
  reference_price: money.optional(),
  actual_quantity: quantity.optional(),
  resolved_quantity: quantity.optional(),
  resolved_spec: z.string().max(1000).optional(),
  evidence: z.string().trim().min(1).max(1000),
  section_rates: z.array(sectionRateSchema).min(1).max(500).optional(),
  length_label: z.string().trim().min(1).max(120).optional(),
  selected_section: z.string().max(100).optional(),
  resolved_section: z.string().max(100).optional(),
}).strict();
export type DimensionPricing = z.infer<typeof dimensionPricingSchema>;
/** Physical consumption varies; the merchant's unit price does not. */
export const dimensionQuantitySchema = dimensionPricingSchema.pick({
  measure: true, reference_quantity: true, dimension_labels: true, evidence: true,
  quantity_basis: true, reference_dimensions: true,
}).extend({
  fixed_quantity: quantity.default(0),
  reference_factor: quantity.optional(),
  resolved_quantity: quantity.optional(),
}).strict();
export type DimensionQuantity = z.infer<typeof dimensionQuantitySchema>;
const material = z.object({
  material_id: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(200),
  spec: z.string().max(500).optional(),
  base_factor: quantity,
  waste_pct: quantity.max(1000),
  quantity_per_unit: quantity.optional(),
  unit: z.string().trim().min(1).max(50),
  unit_price: materialUnitPriceSchema,
  pack_size: quantity.refine(n => n > 0, "Pack size must be positive").optional(),
  round_packs: z.boolean().optional(),
  dimension_pricing: dimensionPricingSchema.optional(),
  quantity_pricing: dimensionQuantitySchema.optional(),
  assumption: z.string().trim().min(1).max(1000),
}).strict();
const common = {
  version: z.literal(1),
  labour_allowance: money,
  plant_allowance: money.default(0),
  labour_pricing: dimensionPricingSchema.optional(),
  plant_pricing: dimensionPricingSchema.optional(),
  materials: z.array(material).max(100),
  original_materials: z.array(material).max(100).optional(),
  status: z.enum(["none", "reviewed", "user_edited"]),
  quote_pricing: z.object({
    selected_rate: money,
    baseline_materials: money,
    baseline_labour: money,
    baseline_plant: money,
    status: z.enum(["calculated", "needs_measurements", "manual"]),
    notes: z.array(z.string().max(2000)).max(205),
  }).strict().optional(),
};
export const costRecipeSchema = z.discriminatedUnion("shape", [
  z.object({ ...common, shape: z.literal("formula") }).strict(),
  z.object({
    ...common, shape: z.literal("specification"),
    specification: z.string().trim().min(1).max(2000),
    supply_allowance: money,
    supply_name: z.string().trim().min(1).max(200).optional(),
    supply_material_id: z.string().trim().min(1).max(100).optional(),
    supply_pricing: dimensionPricingSchema.optional(),
  }).strict(),
]).superRefine((value, ctx) => {
  if (new Set(value.materials.map(m => m.material_id)).size !== value.materials.length) {
    ctx.addIssue({ code: "custom", path: ["materials"], message: "Material IDs must be unique" });
  }
  const models = value.materials.map((m, i) => ({ model: m.dimension_pricing, price: m.unit_price, path: ["materials", i, "dimension_pricing"] as (string | number)[] }));
  if (value.shape === "specification") models.push({ model: value.supply_pricing, price: value.supply_allowance, path: ["supply_pricing"] });
  models.push(
    { model: value.labour_pricing, price: value.labour_allowance, path: ["labour_pricing"] },
    { model: value.plant_pricing, price: value.plant_allowance, path: ["plant_pricing"] },
  );
  for (const key of ["labour_pricing", "plant_pricing"] as const) {
    if (value[key]?.section_rates)
      ctx.addIssue({ code: "custom", path: [key], message: "Installation dependencies use measurements, not supplier section rates" });
  }
  value.materials.forEach((m, i) => {
    if (m.quantity_pricing && m.dimension_pricing)
      ctx.addIssue({ code: "custom", path: ["materials", i], message: "Choose quantity or unit-price dimension pricing, not both" });
    if (m.quantity_pricing) {
      const model = m.quantity_pricing;
      if (model.fixed_quantity > (model.reference_factor ?? m.base_factor))
        ctx.addIssue({ code: "custom", path: ["materials", i, "quantity_pricing"], message: "Fixed quantity cannot exceed reference consumption" });
      // Validate labels/units through the same measurement contract below.
      models.push({ model: {
        measure: model.measure, reference_quantity: model.reference_quantity,
        dimension_labels: model.dimension_labels, fixed_cost: 0, evidence: model.evidence,
        quantity_basis: model.quantity_basis, reference_dimensions: model.reference_dimensions,
      }, price: 0, path: ["materials", i, "quantity_pricing"] });
    }
  });
  for (const { model, price, path } of models) {
    if (!model) continue;
    if (model.reference_dimensions) {
      const refs = model.reference_dimensions;
      const expectedUnit = ({ length: "m", area: "m²", volume: "m³", weight: "kg", count: "each" })[model.measure];
      const labels = refs.map(d => d.label.toLowerCase().trim());
      const product = refs.reduce((n, d) => n * d.value, 1);
      const validUnits = refs.length === 1 ? refs[0].unit === expectedUnit :
        model.measure === "weight" ? refs.some(d => d.unit === "m") && refs.some(d => d.unit === "kg/m") :
        ["area", "volume"].includes(model.measure) && refs.every(d => d.unit === "m");
      if (refs.length !== model.dimension_labels.length || new Set(labels).size !== refs.length
        || model.dimension_labels.some(label => !labels.includes(label.toLowerCase().trim()))
        || !validUnits || Math.abs(product - model.reference_quantity) > 0.00005)
        ctx.addIssue({ code: "custom", path: [...path, "reference_dimensions"],
          message: "Reference axes must match the measurement labels, units and reference quantity" });
    }
    if (model.section_rates) {
      if (!model.length_label || model.fixed_cost !== 0 || model.quantity_basis === "per_work_unit")
        ctx.addIssue({ code: "custom", path, message: "Section pricing needs a length label and no fixed portion" });
      const keys = model.section_rates.map(r => normaliseSectionKey(r.section));
      if (new Set(keys).size !== keys.length)
        ctx.addIssue({ code: "custom", path: [...path, "section_rates"], message: "Section rates must be unique" });
    }
    if (model.fixed_cost > (model.reference_price ?? price))
      ctx.addIssue({ code: "custom", path: [...path, "fixed_cost"], message: "Fixed portion cannot exceed the reference price" });
    if (new Set(model.dimension_labels.map(s => s.toLowerCase().trim())).size !== model.dimension_labels.length)
      ctx.addIssue({ code: "custom", path: [...path, "dimension_labels"], message: "Use distinct dimension labels" });
    // Weight can be an explicit kg measurement or length × linear mass (m × kg/m).
    const count = model.measure === "volume" ? 3 : ["area", "weight"].includes(model.measure) ? 2 : 1;
    if (model.dimension_labels.length !== 1 && model.dimension_labels.length !== count)
      ctx.addIssue({ code: "custom", path: [...path, "dimension_labels"], message: model.measure === "weight"
        ? "Use one measured weight label or two labels for length (m) and linear mass (kg/m)"
        : `Use one measured ${model.measure} label${count > 1 ? ` or ${count} length labels` : ""}` });
  }
});
export type CostRecipe = z.infer<typeof costRecipeSchema>;

/** Admin restoration history stays on the shared library item, never a quote. */
export function recipeForQuoteSnapshot(recipe: CostRecipe): CostRecipe {
  const { original_materials: _originalMaterials, ...snapshot } = recipe;
  return snapshot as CostRecipe;
}
const scaled = (n: number, dp: number) => BigInt(n.toFixed(dp).replace(".", ""));
const roundDivide = (n: bigint, d: bigint) => (n + d / 2n) / d;
const asMoney = (cents: bigint) => `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`;

export function deriveCostRecipe(input: unknown): { recipe: CostRecipe; rateTypical: string } {
  const parsed = costRecipeSchema.parse(input);
  const recipe: CostRecipe = {
    ...parsed,
    materials: parsed.materials.map(m => {
      const q = roundDivide(scaled(m.base_factor, 4) * (1_000_000n + scaled(m.waste_pct, 4)), 1_000_000n);
      if (q > 999_999_999_900n) throw new RangeError("Material quantity is too large");
      return { ...m, quantity_per_unit: Number(q) / 10000 };
    }),
  };
  let amount = (scaled(recipe.labour_allowance, 2) + scaled(recipe.plant_allowance, 2)
    + (recipe.shape === "specification" ? scaled(recipe.supply_allowance, 2) : 0n)) * 100_000_000n;
  for (const m of recipe.materials) amount += scaled(m.quantity_per_unit!, 4) * scaled(m.unit_price, 6);
  const cents = roundDivide(amount, 100_000_000n);
  if (cents > 9_999_999_999n) throw new RangeError("Typical rate exceeds the library limit");
  return { recipe, rateTypical: asMoney(cents) };
}

export function materialExtendedPrice(quantityPerUnit: number, unitPrice: number): string {
  return asMoney(roundDivide(scaled(quantityPerUnit, 4) * scaled(unitPrice, 6), 100_000_000n));
}

export type CostRecipeState = {
  recipe: CostRecipe | null;
  rateTypical: string;
  rateLow?: string;
  rateHigh?: string;
  unit: string;
  recipeSource: "default" | "override" | "custom" | null;
  hasRecipeOverride: boolean;
  legacyOverride: boolean;
  /** Whether the shared task has protected restoration history, not priced materials. */
  hasOriginalMaterials?: boolean;
};

export const recipeRateAdjustmentSchema = z.object({
  rateLow: z.string().regex(/^\d{1,8}(\.\d{1,2})?$/),
  rateTypical: z.string().regex(/^\d{1,8}(\.\d{1,2})?$/),
  rateHigh: z.string().regex(/^\d{1,8}(\.\d{1,2})?$/),
  expectedSnapshot: z.string().min(1).max(1_000_000),
  changeReason: z.string().max(2000).optional(),
}).strict();
export type RecipeRateAdjustment = z.infer<typeof recipeRateAdjustmentSchema>;

/** Compare exactly the effective prices, unit and normalized whole recipe shown before confirmation. */
export function costRecipeSnapshot(state: Pick<CostRecipeState, "unit" | "rateLow" | "rateTypical" | "rateHigh"> & { recipe: unknown }): string {
  const stable = (value: unknown): unknown => Array.isArray(value) ? value.map(stable)
    : value && typeof value === "object"
      ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, v]) => [key, stable(v)]))
      : value;
  return JSON.stringify(stable({
    unit: state.unit,
    rateLow: Number(state.rateLow ?? state.rateTypical).toFixed(2),
    rateTypical: Number(state.rateTypical).toFixed(2),
    rateHigh: Number(state.rateHigh ?? state.rateTypical).toFixed(2),
    recipe: state.recipe ? recipeForQuoteSnapshot(deriveCostRecipe(state.recipe).recipe) : null,
  }));
}

export function scaleCostRate(value: string | number, oldTypical: string | number, newTypical: string | number): string {
  const v = money.parse(Number(value)), old = money.parse(Number(oldTypical)), next = money.parse(Number(newTypical));
  if (old === 0) {
    if (next === 0) return asMoney(scaled(v, 2));
    throw new RangeError("Cannot preserve proportions from a zero Typical rate. Set the rates and breakdown manually first.");
  }
  const cents = roundDivide(scaled(v, 2) * scaled(next, 2), scaled(old, 2));
  if (cents > 9_999_999_999n) throw new RangeError("Scaled rate exceeds the library limit");
  return asMoney(cents);
}

/** Change monetary inputs only. Quantities, waste, specifications and assumptions stay intact. */
export function reconcileRecipeTypical(input: unknown, target: string) {
  const original = deriveCostRecipe(input);
  const scale = (v: number) => Number(scaleCostRate(v, original.rateTypical, target));
  const scaleMaterial = (v: number) => {
    if (money.safeParse(v).success) return scale(v);
    // Retain an existing fractional-penny rate when editing the overall rate.
    if (Number(original.rateTypical) === 0) {
      scaleCostRate(0, original.rateTypical, target); // retain the existing zero-baseline guard
      return v;
    }
    return materialUnitPriceSchema.parse(Number(roundDivide(
      scaled(v, 6) * scaled(money.parse(Number(target)), 2),
      scaled(Number(original.rateTypical), 2),
    )) / 1_000_000);
  };
  const scaleModel = (m: DimensionPricing): DimensionPricing => ({
    ...m, fixed_cost: scale(m.fixed_cost),
    ...(m.reference_price != null ? { reference_price: scale(m.reference_price) } : {}),
  });
  const recipe = {
    ...original.recipe,
    labour_allowance: scale(original.recipe.labour_allowance),
    plant_allowance: scale(original.recipe.plant_allowance),
    ...(original.recipe.labour_pricing ? { labour_pricing: scaleModel(original.recipe.labour_pricing) } : {}),
    ...(original.recipe.plant_pricing ? { plant_pricing: scaleModel(original.recipe.plant_pricing) } : {}),
    materials: original.recipe.materials.map(m => ({ ...m, unit_price: scaleMaterial(m.unit_price) })),
    ...(original.recipe.shape === "specification" ? { supply_allowance: scale(original.recipe.supply_allowance) } : {}),
  };
  const derived = deriveCostRecipe(recipe);
  const difference = Math.round(Number(target) * 100) - Math.round(Number(derived.rateTypical) * 100);
  let roundingAdjustment: { field: string; amount: number } | null = null;
  if (difference) {
    const fields = ["labour_allowance", "plant_allowance", ...(recipe.shape === "specification" ? ["supply_allowance"] : [])] as const;
    const values = recipe as unknown as Record<string, number>;
    const field = fields.find(f => values[f] > 0 && Math.round(values[f] * 100) + difference >= 0);
    if (!field) throw new RangeError("This price cannot be matched exactly by scaling the existing allowances and unit prices. Edit the breakdown manually.");
    values[field] = (Math.round(values[field] * 100) + difference) / 100;
    roundingAdjustment = { field, amount: difference / 100 };
  }
  const result = deriveCostRecipe(recipe);
  if (Number(result.rateTypical) !== Number(target)) throw new RangeError("The breakdown could not be reconciled exactly.");
  return { ...result, roundingAdjustment };
}
